// WHAT: Main app component for Investments workspace with child route navigation
// WHY:  Provides landing page with investment tool cards and child route handling
// DOMAIN: Workspaces → Investments
// DEPENDENCIES: React Router, UI components
// RISKS:
//   - NOTE: Child route detection may not work correctly with nested routes
//   - NOTE: Placeholder cards may confuse users
//   - NOTE: Navigation may not preserve state

import { type FC } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Card } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Main app component that provides landing page for Investments workspace
 *   with child cards for different investment tools and child route handling.
 *
 * WHY:
 *   Enables users to access various investment tracking tools from a central
 *   landing page with clear navigation.
 *
 * BEHAVIOR:
 *   - Displays header with workspace information
 *   - Shows child cards for investment tools (Parqet, etc.)
 *   - Handles child routes via Outlet
 *   - Navigates to child routes on card click
 *   - Logs debug information
 *
 * RISKS:
 *   - NOTE: Child route detection may not work correctly with nested routes
 *   - NOTE: Placeholder cards may confuse users
 *   - NOTE: Navigation may not preserve state
 *
 * @returns InvestmentsApp component
 *
 */
export const InvestmentsApp: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  debugLog("info", "productivity", "InvestmentsApp component rendered", {
    module: "investments",
    path: location.pathname,
  });

  // If we're on a child route, render the outlet
  if (location.pathname.startsWith("/investments/") && location.pathname !== "/investments") {
    return <Outlet />;
  }

  // Default landing page with ChildCards
  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-tracq-bright-blue/20 rounded-lg">
              <svg
                className="w-6 h-6 text-tracq-bright-blue"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Investments</h1>
              <p className="text-slate-400">Portfolio Management & Investment Tracking</p>
            </div>
          </div>
        </div>
      </Card>

      {/* ChildCards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Parqet Card */}
        <Card
          className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 hover:scale-[1.02] cursor-pointer"
          onClick={() => {
            const result = navigate("/investments/parqet");
            // Reason: navigate() may return a Promise in some router versions, handle it if present
            if (result instanceof Promise) {
              result.catch(() => {
                // Navigation errors are handled silently in onClick handlers
              });
            }
          }}
          padding="lg"
          variant="default"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-tracq-bright-blue/20 rounded-lg">
                <svg
                  className="w-6 h-6 text-tracq-bright-blue"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Parqet</h3>
                <p className="text-slate-400 text-sm">Portfolio Tracking</p>
              </div>
            </div>
            <p className="text-slate-300 text-sm">
              Comprehensive portfolio management with securities tracking, transaction history, and
              performance analytics.
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Investment Tracking</span>
              <svg
                className="w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M9 5l7 7-7 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
          </div>
        </Card>

        {/* Placeholder for future cards */}
        <Card
          className="bg-slate-700/20 border-slate-600/30 shadow-tracq-card opacity-50"
          padding="lg"
          variant="default"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-600/30 rounded-lg">
                <svg className="w-6 h-6 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-400">Coming Soon</h3>
                <p className="text-slate-500 text-sm">More Tools</p>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Additional investment tools and features will be added here.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
